/**
 * Account detail modal — opened when an account card is clicked on the
 * kanban. Surfaces the rich info that doesn't fit on the card:
 *
 *   - Primary POC contact info (name / title / email / phone), editable inline
 *   - Owner / assignment
 *   - Per-workflow status lines (the CSV's natural granularity)
 *   - Audit trail (most-recent-first)
 *   - Email trail (most-recent-first)
 *   - Free-form notes
 *
 * The modal owns a single `draft` Account that mirrors the persisted
 * record. Edits mutate the draft locally; "Save" writes through to the
 * accounts adapter (which re-stamps `updatedAt` and emits to all
 * subscribers, so the kanban card refreshes automatically).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AtSign,
  Archive,
  ArchiveRestore,
  BrainCircuit,
  Briefcase,
  Building2,
  Check,
  CheckSquare,
  ChevronDown,
  Edit3,
  FileText,
  FolderKanban,
  History as HistoryIcon,
  Inbox as InboxIcon,
  LayoutGrid,
  ListTodo,
  Loader2,
  Mail,
  Phone,
  Plus,
  Save,
  UserCircle2,
  Video,
  X,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useFocusTrap } from '../lib/useFocusTrap';
import {
  appendAudit,
  appendEmail,
  updateAccount,
} from '../lib/db/accounts';
import {
  bootstrapAppUsersIfEmpty,
  subscribeAppUsers,
} from '../lib/db/appUsers';
import { formatMeetNote, meetInfo, pullMeetingNotes } from '../lib/agent/meetNotes';
import {
  emailDedupeKey,
  emailInfo,
  pullEmailThreads,
  toEmailEntry,
} from '../lib/agent/emailLog';
import { subscribeDocuments } from '../lib/db/documents';
import { PROJECT_STATUSES, createProject, subscribeProjects } from '../lib/db/projects';
import { subscribeTasks } from '../lib/db/tasks';
import { DocumentsPanel } from './DocumentsPanel';
import { RecordTimelinePanel } from './RecordTimelinePanel';
import { TasksPanel } from './TasksPanel';
import {
  BRAIN_SECTIONS,
  SOURCE_META,
  bootstrapBrainIfEmpty,
  sectionLabel,
  subscribeBrain,
  subscribeBrainEvents,
  type BrainView,
} from '../lib/brain/companyBrain';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  STAGE_PHASE,
  accountRollupStage,
  type Account,
  type AppUser,
  type BrainEvent,
  type BusinessDoc,
  type EmailEntry,
  type Project,
  type ProjectStatus,
  type ProjectTask,
} from '../lib/db/types';
import { EnrichPanel } from './EnrichPanel';
import { isConfigured as agentWorkerConfigured } from '../lib/agent/argoWorker';
import {
  accountEnrichRecord,
  commitEnrichField,
  commitEnrichFields,
  fetchAccountWorkerFields,
  revertEnrichField,
} from '../lib/crm/enrichAccountBridge';

interface Props {
  account: Account;
  onClose: () => void;
}

type TabKey =
  | 'overview'
  | 'timeline'
  | 'projects'
  | 'tasks'
  | 'documents'
  | 'brain'
  | 'audit'
  | 'emails';

export function AccountDetailModal({ account, onClose }: Props) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const [draft, setDraft] = useState<Account>(account);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<TabKey>('overview');
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [documents, setDocuments] = useState<BusinessDoc[]>([]);
  const [brain, setBrain] = useState<BrainView | null>(null);
  const [brainEvents, setBrainEvents] = useState<BrainEvent[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  // Subscribe to this account's Company Brain (read-only here). Seeds a
  // believable demo Brain once in mock mode; no-op in production.
  useEffect(() => {
    let active = true;
    bootstrapBrainIfEmpty().catch(() => undefined);
    const unsubBrain = subscribeBrain(account.id, (v) => {
      if (active) setBrain(v);
    });
    const unsubEvents = subscribeBrainEvents(account.id, (e) => {
      if (active) setBrainEvents(e);
    });
    return () => {
      active = false;
      unsubBrain();
      unsubEvents();
    };
  }, [account.id]);

  // Subscribe to documents (proposals + contracts) and keep only the
  // ones attached to this account, newest-first.
  useEffect(() => {
    let active = true;
    const unsub = subscribeDocuments((all) => {
      if (!active) return;
      setDocuments(
        all
          .filter((d) => d.entityType === 'account' && d.entityId === account.id)
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
      );
    });
    return () => {
      active = false;
      unsub();
    };
  }, [account.id]);

  // Subscribe to the appUsers collection — used to populate the
  // Owner combobox so account ownership stays in sync with Admin >
  // Users (and, eventually, Firebase Auth claims).
  useEffect(() => {
    let active = true;
    bootstrapAppUsersIfEmpty().catch(() => undefined);
    const unsub = subscribeAppUsers((users) => {
      if (active) setAppUsers(users);
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);

  // Sync from the upstream account when it changes (e.g. another
  // surface mutated it). If the local draft is dirty, we keep the user's
  // edits and just overlay the bookkeeping fields.
  useEffect(() => {
    if (!dirty) {
      setDraft(account);
    } else {
      setDraft((prev) => ({
        ...prev,
        // Always reflect the freshest audit/email lists; user can't
        // edit these directly so there's no conflict.
        audit: account.audit,
        emails: account.emails,
        updatedAt: account.updatedAt,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // ESC closes (without saving). Click-outside dismisses too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setField = <K extends keyof Account>(key: K, value: Account[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    // Workflow stages are owned by the workflow editor below; if any
    // workflow's stage changed in the draft we log a single audit
    // entry per change so the timeline reflects the activity.
    const stageDiffs: string[] = [];
    for (const before of account.workflows) {
      const after = draft.workflows.find((w) => w.id === before.id);
      if (after && after.stage !== before.stage) {
        stageDiffs.push(`"${before.name}": ${before.stage} → ${after.stage}`);
      }
    }
    const patch: Partial<Account> = {
      name: draft.name,
      pocName: draft.pocName,
      pocTitle: draft.pocTitle,
      pocEmail: draft.pocEmail,
      pocPhone: draft.pocPhone,
      ownerName: draft.ownerName,
      assignedTo: draft.assignedTo,
      notes: draft.notes,
      workflows: draft.workflows,
    };
    await updateAccount(account.id, patch);
    for (const text of stageDiffs) {
      await appendAudit(account.id, `Workflow stage changed — ${text}`);
    }
    setDirty(false);
  };

  const handleArchive = async () => {
    const next = !draft.archived;
    if (next && !window.confirm(`Archive ${account.name}? It's hidden from the Clients directory but the record is kept — you can restore it from here.`)) {
      return;
    }
    await updateAccount(account.id, { archived: next });
    setDraft((d) => ({ ...d, archived: next }));
    if (next) onClose();
  };

  // --- Email-trail entry --------------------------------------------------

  const [emailDraft, setEmailDraft] = useState<Partial<EmailEntry>>({
    direction: 'out',
  });
  const submitEmail = async () => {
    if (!emailDraft.subject?.trim()) return;
    await appendEmail(account.id, {
      at: new Date().toISOString(),
      direction: emailDraft.direction ?? 'out',
      subject: emailDraft.subject!.trim(),
      from: emailDraft.from?.trim() || undefined,
      to: emailDraft.to?.trim() || undefined,
      preview: emailDraft.preview?.trim() || undefined,
    });
    setEmailDraft({ direction: 'out' });
  };

  // --- Audit entry --------------------------------------------------------

  const [auditDraft, setAuditDraft] = useState('');
  const submitAudit = async () => {
    const text = auditDraft.trim();
    if (!text) return;
    await appendAudit(account.id, text);
    setAuditDraft('');
  };

  // Pull Google Meet notes for this account via the GSuite seam (live
  // worker when configured, deterministic simulation otherwise) and
  // append each as an audit entry. Returns the count for the button.
  const syncMeet = async (): Promise<number> => {
    const notes = await pullMeetingNotes({
      accountName: account.name,
      name: account.pocName,
      email: account.pocEmail,
    });
    const actor = meetInfo().configured ? 'Google Meet' : 'Google Meet (simulated)';
    for (const note of [...notes].sort((a, b) => a.at.localeCompare(b.at))) {
      await appendAudit(account.id, formatMeetNote(note), actor);
    }
    return notes.length;
  };

  // Pull recent Gmail threads for this account via the same GSuite seam
  // and append the new ones to the email trail. Idempotent: existing
  // entries (matched on direction + subject + day) are skipped so a
  // repeat sync never duplicates the thread. Returns the count of newly
  // added messages for the button copy.
  const syncEmail = async (): Promise<number> => {
    const messages = await pullEmailThreads({
      accountName: account.name,
      name: account.pocName,
      email: account.pocEmail,
    });
    const seen = new Set(draft.emails.map(emailDedupeKey));
    const fresh = messages.filter((m) => !seen.has(emailDedupeKey(m)));
    // Oldest-first so the most-recent message ends up last-appended.
    for (const msg of [...fresh].sort((a, b) => a.at.localeCompare(b.at))) {
      await appendEmail(account.id, toEmailEntry(msg));
    }
    return fresh.length;
  };

  // --- Rollup stage + phase color -----------------------------------------
  //
  // Account-level stage is derived from the per-workflow stages
  // (earliest in canonical order). The detail modal surfaces this as
  // a read-only pill — to actually change stages the user moves
  // workflows on the kanban OR edits each workflow's stage in the
  // Workflows tab below.
  const rollupStage = useMemo(() => accountRollupStage(draft), [draft]);
  const phase = rollupStage ? STAGE_PHASE[rollupStage] : null;
  const phaseAccent = useMemo(() => {
    if (phase === 'sales') return C.magenta;
    if (phase === 'implementations') return C.blueElectric;
    if (phase === 'customer-success') return C.green;
    return C.textDim;
  }, [phase]);

  // --- Render --------------------------------------------------------------

  const modal = (
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-modal-title"
        tabIndex={-1}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header className="account-modal__head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className="t-eyebrow">Account</span>
              <input
                id="account-modal-title"
                className="account-modal__name"
                value={draft.name}
                onChange={(e) => setField('name', e.target.value)}
                readOnly={!canMutate}
                aria-label="Account name"
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 4,
                  flexWrap: 'wrap',
                }}
              >
                {rollupStage ? (
                  <span
                    className="glass-pill"
                    style={{
                      color: phaseAccent,
                      borderColor: 'rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.04)',
                      fontWeight: 700,
                    }}
                    title="Earliest stage across this account's workflows"
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: phaseAccent,
                        marginRight: 4,
                      }}
                    />
                    Rollup: {rollupStage}
                  </span>
                ) : (
                  <span className="t-meta">No workflows yet</span>
                )}
                <span className="t-meta">
                  Updated {fmtAbsolute(draft.updatedAt)} · Created{' '}
                  {fmtAbsolute(draft.createdAt)}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={13} />
            </button>
          </header>

          {/* Tabs */}
          <nav className="account-modal__tabs" role="tablist">
            {(
              [
                { id: 'overview', label: 'Overview', icon: UserCircle2 },
                { id: 'timeline', label: 'Timeline', icon: HistoryIcon },
                { id: 'projects', label: 'Projects', icon: FolderKanban },
                { id: 'tasks', label: 'Tasks', icon: ListTodo },
                { id: 'documents', label: 'Documents', icon: FileText },
                { id: 'brain', label: 'Brain', icon: BrainCircuit },
                { id: 'audit', label: 'Audit trail', icon: CheckSquare },
                { id: 'emails', label: 'Emails', icon: Mail },
              ] as { id: TabKey; label: string; icon: typeof X }[]
            ).map((t) => {
              const Icon = t.icon;
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className="account-modal__tab"
                  data-active={isActive}
                  onClick={() => setTab(t.id)}
                >
                  <Icon size={13} />
                  <span>{t.label}</span>
                  {t.id === 'brain' && brain && brain.factCount > 0 && (
                    <span className="account-modal__tab-count">
                      {brain.factCount}
                    </span>
                  )}
                  {t.id === 'documents' && documents.length > 0 && (
                    <span className="account-modal__tab-count">
                      {documents.length}
                    </span>
                  )}
                  {t.id === 'audit' && (
                    <span className="account-modal__tab-count">
                      {draft.audit.length}
                    </span>
                  )}
                  {t.id === 'emails' && (
                    <span className="account-modal__tab-count">
                      {draft.emails.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Body */}
          <div className="account-modal__body">
            {tab === 'overview' && (
              <OverviewTab
                draft={draft}
                onChange={setField}
                appUsers={appUsers}
                canMutate={canMutate}
              />
            )}
            {tab === 'timeline' && (
              <RecordTimelinePanel account={draft} canMutate={canMutate} />
            )}
            {tab === 'projects' && (
              <AccountProjectsTab
                accountId={draft.id}
                accountName={draft.name}
                canMutate={canMutate}
              />
            )}
            {tab === 'brain' && (
              <BrainTab brain={brain} events={brainEvents} />
            )}
            {tab === 'documents' && (
              <DocumentsPanel
                entityType="account"
                entityId={draft.id}
                entityName={draft.name}
                contactName={draft.pocName}
                ownerName={draft.ownerName}
                documents={documents}
              />
            )}
            {tab === 'tasks' && (
              <TasksPanel
                entityType="account"
                entityId={draft.id}
                entityName={draft.name}
              />
            )}
            {tab === 'audit' && (
              <AuditTab
                entries={draft.audit}
                draft={auditDraft}
                onChangeDraft={setAuditDraft}
                onSubmit={submitAudit}
                onSyncMeet={syncMeet}
                canMutate={canMutate}
              />
            )}
            {tab === 'emails' && (
              <EmailsTab
                emails={draft.emails}
                draft={emailDraft}
                onChangeDraft={setEmailDraft}
                onSubmit={submitEmail}
                onSyncEmail={syncEmail}
                canMutate={canMutate}
              />
            )}
          </div>

          {/* Footer */}
          <footer className="account-modal__foot">
            {canMutate ? (
              <button
                className="glass-btn"
                onClick={handleArchive}
                title={draft.archived ? 'Restore account' : 'Archive account'}
              >
                {draft.archived ? (
                  <><ArchiveRestore size={13} /> Restore</>
                ) : (
                  <><Archive size={13} /> Archive</>
                )}
              </button>
            ) : (
              <span className="t-meta">Read-only · viewer access</span>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="glass-btn" onClick={onClose}>
                Close
              </button>
              {canMutate && (
                <button
                  className="glass-btn glass-btn--primary"
                  onClick={handleSave}
                  disabled={!dirty}
                  style={{ opacity: dirty ? 1 : 0.55 }}
                >
                  <Save size={13} /> Save changes
                </button>
              )}
            </div>
          </footer>
        </GlassPanel>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// --- Tabs -----------------------------------------------------------------

function OverviewTab({
  draft,
  onChange,
  appUsers,
  canMutate,
}: {
  draft: Account;
  onChange: <K extends keyof Account>(key: K, value: Account[K]) => void;
  appUsers: AppUser[];
  canMutate: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Section title="Primary point of contact" icon={UserCircle2}>
        <FieldGrid>
          <Edit
            label="Name"
            icon={UserCircle2}
            value={draft.pocName}
            placeholder="Add primary POC"
            onChange={(v) => onChange('pocName', v)}
            readOnly={!canMutate}
          />
          <Edit
            label="Title"
            icon={Briefcase}
            value={draft.pocTitle}
            onChange={(v) => onChange('pocTitle', v)}
            readOnly={!canMutate}
          />
          <Edit
            label="Email"
            icon={AtSign}
            value={draft.pocEmail}
            placeholder="name@company.com"
            onChange={(v) => onChange('pocEmail', v)}
            readOnly={!canMutate}
          />
          <Edit
            label="Phone"
            icon={Phone}
            value={draft.pocPhone}
            placeholder="+1 …"
            onChange={(v) => onChange('pocPhone', v)}
            readOnly={!canMutate}
          />
        </FieldGrid>
      </Section>

      <Section title="Assignment" icon={Building2}>
        <FieldGrid>
          <OwnerCombobox
            label="Owner"
            value={draft.ownerName}
            users={appUsers}
            onChange={(v) => onChange('ownerName', v)}
            readOnly={!canMutate}
          />
          <Edit
            label="Assigned to"
            icon={UserCircle2}
            value={draft.assignedTo}
            placeholder="Free-form team or shared owners"
            onChange={(v) => onChange('assignedTo', v)}
            readOnly={!canMutate}
          />
        </FieldGrid>
      </Section>

      <Section title="Notes" icon={Edit3}>
        <textarea
          className="glass-input"
          value={draft.notes ?? ''}
          rows={5}
          onChange={(e) => onChange('notes', e.target.value)}
          readOnly={!canMutate}
          placeholder={
            canMutate
              ? 'Anything worth remembering — context, last conversation, next step…'
              : 'No notes.'
          }
          style={{
            width: '100%',
            padding: '10px 12px',
            fontFamily: 'Manrope, sans-serif',
            lineHeight: 1.5,
          }}
        />
      </Section>

      {/* Governed enrichment — before/after diffs through the approvals
          seal. Worker leg (argoWorker) is injected; unconfigured/failed
          runs fall back to the deterministic no-hallucination pipeline. */}
      <EnrichPanel
        record={accountEnrichRecord(draft)}
        fetchWorkerFields={() => fetchAccountWorkerFields(draft)}
        workerConfigured={agentWorkerConfigured()}
        onApprove={(field, diff) => commitEnrichField(draft, field, diff)}
        onAutoApply={(fields, diff) => commitEnrichFields(draft, fields, diff)}
        onRevert={(field, diff) => revertEnrichField(draft, field, diff)}
        disabled={!canMutate}
      />
    </div>
  );
}

const ACCT_PROJECT_STATUS_COLOR: Record<ProjectStatus, string> = {
  active: C.green,
  'on-hold': C.orange,
  completed: C.blueLight,
  archived: C.textDim,
};

/**
 * Projects tab for the account modal — the account's delivery projects in a
 * list or kanban-by-status view, with inline add. Replaces the archived
 * Workflows tab (feature overlap with the Projects surface). Read-derived
 * completion %, never stored.
 */
function AccountProjectsTab({
  accountId,
  accountName,
  canMutate,
}: {
  accountId: string;
  accountName: string;
  canMutate: boolean;
}) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsubP = subscribeProjects((r) => setProjects(r.filter((p) => p.clientId === accountId)));
    const unsubT = subscribeTasks(setTasks);
    return () => {
      unsubP();
      unsubT();
    };
  }, [accountId]);

  const completion = (projectId: string) => {
    const mine = tasks.filter((t) => t.projectId === projectId);
    const done = mine.filter((t) => t.status === 'done').length;
    return { done, total: mine.length, pct: mine.length ? Math.round((done / mine.length) * 100) : 0 };
  };

  const sorted = [...projects].sort((a, b) => a.title.localeCompare(b.title));

  const addProject = async () => {
    const title = newTitle.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      await createProject({ clientId: accountId, clientName: accountName, title });
      setNewTitle('');
    } finally {
      setBusy(false);
    }
  };

  const StatusPill = ({ status }: { status: ProjectStatus }) => {
    const color = ACCT_PROJECT_STATUS_COLOR[status];
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          padding: '2px 8px',
          borderRadius: 999,
          color,
          background: `${color}22`,
          border: `1px solid ${color}55`,
          whiteSpace: 'nowrap',
        }}
      >
        {status}
      </span>
    );
  };

  const Bar = ({ pct }: { pct: number }) => (
    <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${C.magentaDeep}, ${C.magenta})` }} />
    </div>
  );

  const ProjectCardMini = ({ p }: { p: Project }) => {
    const { done, total, pct } = completion(p.id);
    return (
      <GlassPanel variant="subtle" animate={false} style={{ padding: 10, display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <span className="t-card-title">{p.title}</span>
          <StatusPill status={p.status} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Bar pct={pct} />
          </div>
          <span className="t-meta" style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>{pct}% · {done}/{total}</span>
        </div>
      </GlassPanel>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <p className="t-body" style={{ color: C.textMuted, margin: 0 }}>
          Delivery projects for {accountName}. Full detail lives on the Projects surface.
        </p>
        <div style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, overflow: 'hidden' }}>
          {(['list', 'kanban'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className="glass-btn"
              onClick={() => setView(v)}
              title={v === 'list' ? 'List' : 'Kanban by status'}
              style={{
                padding: '6px 9px',
                borderRadius: 0,
                border: 'none',
                background: view === v ? 'rgba(255,0,255,0.18)' : 'transparent',
                color: view === v ? '#fff' : C.textMuted,
              }}
            >
              {v === 'list' ? <ListTodo size={14} /> : <LayoutGrid size={14} />}
            </button>
          ))}
        </div>
      </div>

      {canMutate && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="glass-input"
            placeholder="New project title…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void addProject();
              }
            }}
          />
          <button className="glass-btn" onClick={() => void addProject()} disabled={!newTitle.trim() || busy} style={{ opacity: !newTitle.trim() || busy ? 0.5 : 1, whiteSpace: 'nowrap' }}>
            <Plus size={13} /> Add project
          </button>
        </div>
      )}

      {sorted.length === 0 ? (
        <div
          className="t-meta"
          style={{ padding: '20px 12px', textAlign: 'center', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 12 }}
        >
          No projects for this client yet.
        </div>
      ) : view === 'kanban' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, alignItems: 'start' }}>
          {PROJECT_STATUSES.map((status) => {
            const group = sorted.filter((p) => p.status === status);
            return (
              <div key={status} style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StatusPill status={status} />
                  <span className="t-meta" style={{ color: C.textDim }}>{group.length}</span>
                </div>
                {group.map((p) => (
                  <ProjectCardMini key={p.id} p={p} />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {sorted.map((p) => {
            const { done, total, pct } = completion(p.id);
            return (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '9px 12px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span className="t-card-title" style={{ flex: '2 1 160px', minWidth: 0 }}>{p.title}</span>
                <StatusPill status={p.status} />
                <span className="t-meta" style={{ flex: '0 0 52px', color: C.textMuted }}>{p.priority}</span>
                <div style={{ flex: '1 1 120px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 90 }}>
                  <div style={{ flex: 1 }}>
                    <Bar pct={pct} />
                  </div>
                  <span className="t-meta" style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>{pct}% · {done}/{total}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AuditTab({
  entries,
  draft,
  onChangeDraft,
  onSubmit,
  onSyncMeet,
  canMutate,
}: {
  entries: Account['audit'];
  draft: string;
  onChangeDraft: (s: string) => void;
  onSubmit: () => void;
  onSyncMeet: () => Promise<number>;
  canMutate: boolean;
}) {
  const ordered = [...entries].sort((a, b) => (a.at < b.at ? 1 : -1));
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const syncMeet = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const n = await onSyncMeet();
      setSyncMsg(n > 0 ? `Synced ${n} Meet note${n === 1 ? '' : 's'}` : 'No new Meet notes');
    } catch {
      setSyncMsg('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {canMutate && (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="glass-input"
              value={draft}
              onChange={(e) => onChangeDraft(e.target.value)}
              placeholder="Log a note for the audit trail…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmit();
                }
              }}
            />
            <button
              className="glass-btn glass-btn--primary"
              onClick={onSubmit}
              disabled={!draft.trim()}
            >
              <Plus size={13} /> Log
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="glass-btn"
              onClick={() => void syncMeet()}
              disabled={syncing}
              title="Pull recent Google Meet notes into this audit trail"
            >
              {syncing ? (
                <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
              ) : (
                <Video size={12} />
              )}
              {syncing ? 'Syncing…' : 'Sync Google Meet notes'}
            </button>
            {syncMsg && (
              <span className="t-meta" style={{ color: C.textDim }}>{syncMsg}</span>
            )}
          </div>
        </>
      )}
      <ol className="audit-list">
        {ordered.map((e) => (
          <li key={e.id} className="audit-item">
            <div className="audit-item__dot" aria-hidden="true" />
            <div style={{ flex: 1 }}>
              <div className="t-body" style={{ color: C.text }}>
                {e.text}
              </div>
              <div className="t-meta">
                {fmtAbsolute(e.at)}
                {e.actor ? ` · ${e.actor}` : ''}
              </div>
            </div>
          </li>
        ))}
        {ordered.length === 0 && (
          <li
            className="t-meta"
            style={{ padding: '12px 0', listStyle: 'none' }}
          >
            No audit entries yet.
          </li>
        )}
      </ol>
    </div>
  );
}

function EmailsTab({
  emails,
  draft,
  onChangeDraft,
  onSubmit,
  onSyncEmail,
  canMutate,
}: {
  emails: EmailEntry[];
  draft: Partial<EmailEntry>;
  onChangeDraft: (s: Partial<EmailEntry>) => void;
  onSubmit: () => void;
  onSyncEmail: () => Promise<number>;
  canMutate: boolean;
}) {
  const ordered = [...emails].sort((a, b) => (a.at < b.at ? 1 : -1));
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  // Honest pill: live Gmail vs deterministic local simulation.
  const emailConfigured = emailInfo().configured;

  const syncEmail = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const n = await onSyncEmail();
      setSyncMsg(
        n > 0
          ? `Synced ${n} message${n === 1 ? '' : 's'}`
          : 'No new messages',
      );
    } catch {
      setSyncMsg('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Gmail sync — pull recent threads into the trail via the GSuite
          seam. Honest pill states whether it's live Gmail or simulation. */}
      {canMutate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="glass-btn"
            onClick={() => void syncEmail()}
            disabled={syncing}
            title="Pull recent Gmail threads into this email trail"
          >
            {syncing ? (
              <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
            ) : (
              <Mail size={12} />
            )}
            {syncing ? 'Syncing…' : 'Sync Gmail'}
          </button>
          <span
            className={`glass-pill ${emailConfigured ? 'glass-pill--green' : ''}`}
            title={
              emailConfigured
                ? 'Connected to the Google Workspace worker (VITE_GSUITE_API_URL).'
                : 'No GSuite worker configured — Gmail sync returns deterministic simulated threads.'
            }
            style={{ fontSize: 10 }}
          >
            {emailConfigured ? 'Gmail · connected' : 'Gmail · simulated'}
          </span>
          {syncMsg && (
            <span className="t-meta" style={{ color: C.textDim }}>{syncMsg}</span>
          )}
        </div>
      )}

      {canMutate && (
        <div
          style={{
            padding: 12,
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
            display: 'grid',
            gap: 8,
          }}
        >
          <div className="t-eyebrow">Log an email</div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 8 }}>
            <select
              className="glass-input"
              value={draft.direction ?? 'out'}
              onChange={(e) =>
                onChangeDraft({ ...draft, direction: e.target.value as 'in' | 'out' })
              }
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <option value="out">Outbound</option>
              <option value="in">Inbound</option>
            </select>
            <input
              className="glass-input"
              placeholder="From"
              value={draft.from ?? ''}
              onChange={(e) => onChangeDraft({ ...draft, from: e.target.value })}
            />
            <input
              className="glass-input"
              placeholder="To"
              value={draft.to ?? ''}
              onChange={(e) => onChangeDraft({ ...draft, to: e.target.value })}
            />
          </div>
          <input
            className="glass-input"
            placeholder="Subject"
            value={draft.subject ?? ''}
            onChange={(e) => onChangeDraft({ ...draft, subject: e.target.value })}
          />
          <textarea
            className="glass-input"
            placeholder="Preview / body snippet"
            value={draft.preview ?? ''}
            rows={2}
            onChange={(e) => onChangeDraft({ ...draft, preview: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontFamily: 'Manrope, sans-serif',
              lineHeight: 1.5,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="glass-btn glass-btn--primary"
              disabled={!draft.subject?.trim()}
              onClick={onSubmit}
            >
              <Plus size={13} /> Log email
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {ordered.length === 0 && (
          <div
            className="t-meta"
            style={{
              padding: '24px 12px',
              textAlign: 'center',
              border: '1px dashed rgba(255,255,255,0.08)',
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <InboxIcon size={20} strokeWidth={1.75} />
            {canMutate
              ? 'No emails logged yet. Add one above, or hit Sync Gmail to pull recent threads into the trail.'
              : 'No emails logged yet.'}
          </div>
        )}
        {ordered.map((e) => (
          <div
            key={e.id}
            style={{
              padding: 12,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.04)',
              display: 'grid',
              gap: 4,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  className={`glass-pill ${
                    e.direction === 'in' ? 'glass-pill--blue' : 'glass-pill--magenta'
                  }`}
                >
                  {e.direction === 'in' ? 'Inbound' : 'Outbound'}
                </span>
                <span className="t-card-title">{e.subject}</span>
              </div>
              <span className="t-meta">{fmtAbsolute(e.at)}</span>
            </div>
            <div className="t-meta">
              {[e.from && `From: ${e.from}`, e.to && `To: ${e.to}`]
                .filter(Boolean)
                .join(' · ') || '—'}
            </div>
            {e.preview && (
              <div className="t-body" style={{ color: C.textMuted }}>
                {e.preview}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Tiny field primitives ------------------------------------------------

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof X;
  children: React.ReactNode;
}) {
  return (
    <section style={{ display: 'grid', gap: 8 }}>
      <div
        className="t-eyebrow"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <Icon size={11} /> {title}
      </div>
      {children}
    </section>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {children}
    </div>
  );
}

function Edit({
  label,
  icon: Icon,
  value,
  placeholder,
  onChange,
  readOnly,
}: {
  label: string;
  icon: typeof X;
  value?: string;
  placeholder?: string;
  onChange: (v: string | undefined) => void;
  readOnly?: boolean;
}) {
  return (
    <div>
      <div
        className="t-meta"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 4 }}
      >
        <Icon size={11} /> {label}
      </div>
      <input
        className="glass-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder={readOnly ? undefined : placeholder}
        readOnly={readOnly}
      />
    </div>
  );
}

// --- Owner combobox ------------------------------------------------------
//
// Typeahead-style picker over the `appUsers` collection. Behaves like
// a free-text input (so older accounts that reference a name we don't
// recognise still surface their value) but offers an inline-filtered
// dropdown of known users on focus or on a meaningful query.
function OwnerCombobox({
  label,
  value,
  users,
  onChange,
  readOnly,
}: {
  label: string;
  value?: string;
  users: AppUser[];
  onChange: (v: string | undefined) => void;
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string>(value ?? '');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the input text in sync when the parent draft changes (e.g.
  // the modal reopens for a different account).
  useEffect(() => {
    setQuery(value ?? '');
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const eligible = users.filter((u) => u.status !== 'disabled');
    const ranked = eligible
      .filter((u) => {
        if (!q) return true;
        return (
          u.displayName.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    return ranked.slice(0, 8);
  }, [users, query]);

  const commit = (next: string | undefined) => {
    setQuery(next ?? '');
    onChange(next && next.trim().length > 0 ? next : undefined);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        className="t-meta"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 4,
        }}
      >
        <UserCircle2 size={11} /> {label}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          className="glass-input"
          value={query}
          readOnly={readOnly}
          onChange={(e) => {
            if (readOnly) return;
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => {
            if (!readOnly) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
              setOpen(true);
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const pick = filtered[highlight];
              if (pick) commit(pick.displayName);
              else commit(query);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={readOnly ? undefined : 'Type a name or pick from your team'}
          style={{ paddingRight: 30 }}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {!readOnly && (
          <ChevronDown
            size={13}
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: C.textDim,
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
      {open && !readOnly && (
        <div
          role="listbox"
          aria-label="Argo team members"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 30,
            maxHeight: 240,
            overflowY: 'auto',
            padding: 4,
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.10)',
            background:
              'linear-gradient(135deg, rgba(28,18,38,0.96), rgba(40,18,52,0.92))',
            boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {filtered.length === 0 ? (
            <div
              className="t-meta"
              style={{ padding: '10px 12px', textAlign: 'center' }}
            >
              No matching team members. Press Enter to keep "{query}".
            </div>
          ) : (
            filtered.map((u, i) => {
              const active = highlight === i;
              const isPicked = (value ?? '') === u.displayName;
              return (
                <button
                  key={u.id}
                  type="button"
                  role="option"
                  aria-selected={isPicked}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    // mouseDown so we beat the input's onBlur close.
                    e.preventDefault();
                    commit(u.displayName);
                  }}
                  className="glass-row"
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid transparent',
                    background: active
                      ? 'rgba(255,0,255,0.10)'
                      : 'transparent',
                    boxShadow: active
                      ? 'inset 3px 0 0 rgba(255,0,255,0.55)'
                      : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'inline-grid',
                      placeItems: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#fff',
                      background:
                        u.role === 'admin'
                          ? 'linear-gradient(135deg, #FF00FF, #BB4198)'
                          : u.role === 'staff'
                            ? 'linear-gradient(135deg, #0082F3, #2F01FD)'
                            : 'linear-gradient(135deg, #FDAD01, #E23939)',
                      border: '1px solid rgba(255,255,255,0.18)',
                    }}
                  >
                    {u.displayName.slice(0, 1).toUpperCase()}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      className="t-card-title"
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {u.displayName}
                    </span>
                    <span
                      className="t-meta"
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {u.email} · {u.role}
                    </span>
                  </span>
                  {isPicked && <Check size={13} color={C.magentaSoft} />}
                </button>
              );
            })
          )}
          {value && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                commit(undefined);
              }}
              className="glass-row"
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                borderRadius: 10,
                marginTop: 4,
                background: 'transparent',
                color: C.textMuted,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderTop: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <X size={12} /> Clear owner
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --- Company Brain (read-only) -------------------------------------------

/**
 * Read-only view of the account's Company Brain. Renders every section in
 * canonical order — empty sections included so "unknown" stays a visible,
 * honest state — with a provenance chip on each fact (source + confidence).
 * A change log at the bottom shows the append-only history. No writes here:
 * Phase 1 is observe-only; the Orchestrator becomes the writer later.
 */
function BrainTab({
  brain,
  events,
}: {
  brain: BrainView | null;
  events: BrainEvent[];
}) {
  if (!brain || brain.factCount === 0) {
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        <BrainHeader factCount={0} />
        <div
          className="t-meta"
          style={{
            padding: '28px 12px',
            textAlign: 'center',
            border: '1px dashed rgba(255,255,255,0.08)',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <BrainCircuit size={22} strokeWidth={1.75} />
          This account's Brain is empty. As agents research, meet, and close,
          they'll fill it in here — every fact tagged with where it came from.
        </div>
      </div>
    );
  }

  const populated = brain.sections.filter((s) => s.facts.length > 0);
  const emptyLabels = brain.sections
    .filter((s) => s.facts.length === 0)
    .map((s) => s.meta.label);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <BrainHeader factCount={brain.factCount} />

      {populated.map((s) => (
        <Section key={s.meta.id} title={s.meta.label} icon={BrainCircuit}>
          <div style={{ display: 'grid', gap: 8 }}>
            {s.facts.map((f) => (
              <div
                key={f.id}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.04)',
                  display: 'grid',
                  gap: 6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span className="t-eyebrow">{f.label}</span>
                  <ProvenanceChip source={f.source} confidence={f.confidence} />
                </div>
                <div style={{ color: C.text, lineHeight: 1.45 }}>{f.value}</div>
                <div className="t-meta" style={{ color: C.textDim, fontSize: 11 }}>
                  {f.setBy} · {fmtAbsolute(f.setAt)}
                  {f.version > 1 ? ` · v${f.version}` : ''}
                  {f.evidence ? ` · ${f.evidence}` : ''}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ))}

      {emptyLabels.length > 0 && (
        <div className="t-meta" style={{ color: C.textDim, fontSize: 11 }}>
          Still unknown: {emptyLabels.join(' · ')}
        </div>
      )}

      {events.length > 0 && (
        <Section title="Change log" icon={HistoryIcon}>
          <div style={{ display: 'grid', gap: 6 }}>
            {events.slice(0, 12).map((e) => (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontSize: 12,
                  color: C.textMuted,
                }}
              >
                <span>
                  {e.conflict ? '⚠ conflict on ' : ''}
                  <strong style={{ color: C.text, fontWeight: 600 }}>
                    {sectionLabel(e.section)} · {e.field}
                  </strong>{' '}
                  {e.conflict ? 'left unchanged' : e.oldValue ? 'updated' : 'set'} by{' '}
                  {e.agent}
                </span>
                <span style={{ color: C.textDim, whiteSpace: 'nowrap' }}>
                  {fmtAbsolute(e.at)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function BrainHeader({ factCount }: { factCount: number }) {
  return (
    <div style={{ display: 'grid', gap: 4 }} data-facts={factCount}>
      <div
        className="t-eyebrow"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <BrainCircuit size={13} /> Company Brain
      </div>
      <p className="t-meta" style={{ color: C.textDim, margin: 0, fontSize: 11 }}>
        The per-account source of truth every agent reads from. Each fact carries
        its provenance — where it came from and how sure we are. Read-only for now;
        the Orchestrator writes here as it works.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
        {BRAIN_SECTIONS.map((s) => (
          <span
            key={s.id}
            className="glass-pill"
            title={s.blurb}
            style={{ fontSize: 10, color: C.textMuted }}
          >
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ProvenanceChip({
  source,
  confidence,
}: {
  source: keyof typeof SOURCE_META;
  confidence: number;
}) {
  const meta = SOURCE_META[source];
  const pct = Math.round(confidence * 100);
  return (
    <span
      title={`${meta.hint} (${pct}% confidence)`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: meta.color,
        border: `1px solid ${meta.color}55`,
        background: `${meta.color}1a`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: meta.color,
          display: 'inline-block',
        }}
      />
      {meta.label} · {pct}%
    </span>
  );
}

// --- Helpers -------------------------------------------------------------

function fmtAbsolute(iso: string): string {
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) +
      ' · ' +
      d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    );
  } catch {
    return iso;
  }
}
